/**
 * Who sees the admin entry and which admin pages their menus list.
 *
 * `full`: an administrator role (the whole admin area, minus platform-only pages — config/adminAccess.ts).
 * `courseCreator`: no administrator role, but may create courses — tenant-wide or only in some categories through
 * category roles (useScopedCapabilities). They get the Admin entry with just the course pages they can use.
 */
import { useCallback } from 'react';
import { useAuth } from '@/contexts/shared/AuthContext';
import { canOpenAdminPage, canOpenCreatorPage, hasFullAdminAccess } from '@/config/adminAccess';
import { useScopedCapabilities } from '@/hooks/useScopedCapabilities';

export const useAdminAccess = () => {
  const { user, isImpersonating } = useAuth();
  const full = !!user && hasFullAdminAccess(user as never);
  const { data: caps, isLoading: capsLoading } = useScopedCapabilities(!!user && !full);
  const courseCreator = !full && !!caps?.can_create_course;
  const canOpen = useCallback(
    (path: string) => (courseCreator ? canOpenCreatorPage(caps, path) : canOpenAdminPage(user as never, path)),
    [courseCreator, caps, user],
  );
  return { full, courseCreator, showAdmin: !isImpersonating && (full || courseCreator), canOpen, caps, capsLoading };
};
